// === Backend API contract (mirrors UsageReportRequestDto / UsageReportPageDto) ===

export type UsageReportPeriod = 'LAST_MONTH' | 'LAST_QUARTER' | 'LAST_6_MONTHS' | 'LAST_YEAR';
export type UsageReportSortField = 'SERVICE_NAME' | 'DEPLOYED_AT' | 'LAST_USED' | 'HITS' | 'MISSES' | 'TOTAL';
export type UsageReportStatus = 'ACTIVE' | 'IDLE' | 'NEVER_USED' | 'DELETED';
export type UsageReportSortDirection = 'ASC' | 'DESC';

export interface UsageReportSort {
  field: UsageReportSortField;
  direction: UsageReportSortDirection;
}

export interface UsageReportRequest {
  period?: UsageReportPeriod;
  /** Custom range start (inclusive). ISO-8601 seconds-precision UTC: 'yyyy-MM-ddTHH:mm:ssZ'. Wins over period when both from/to are set. */
  from?: string;
  /** Custom range end (exclusive). Same format as `from`. */
  to?: string;
  workspaceIds?: number[];
  search?: string;
  includeDeletedMocks?: boolean;
  page?: number;
  size?: number;
  sort?: UsageReportSort[];
}

export interface UsageReportRowApi {
  serviceMockId: number;
  serviceName: string;
  workspaceId: number;
  locationId: number;
  locationName: string;
  createdBy: string;
  deployedAt: string | null;
  lastUsed: string | null;
  hits: number;
  misses: number;
  total: number;
  status: UsageReportStatus;
}

export interface UsageReportPage {
  rows: UsageReportRowApi[];
  totalRows: number;
  filteredRows: number;
  page: number;
  size: number;
}

// === UI state ===

export type PeriodPreset = UsageReportPeriod;

export type PeriodSelection = { kind: 'preset'; value: PeriodPreset } | { kind: 'custom'; from: Date; to: Date };

export interface UsageReportRow {
  id: string;
  serviceMockId: number;
  serviceName: string;
  workspaceId: number;
  workspaceName: string;
  locationName: string;
  createdBy: string;
  deployedAt: string | null;
  lastUsedAt: string | null;
  hits: number;
  misses: number;
  total: number;
  status: UsageReportStatus;
}

export interface UsageWorkspace {
  id: number;
  name: string;
}

export interface UsageFilters {
  period: PeriodSelection;
  workspaceIds: number[];
  searchTerm: string;
  includeDeletedMocks: boolean;
}

export const DEFAULT_USAGE_FILTERS: UsageFilters = {
  period: { kind: 'preset', value: 'LAST_QUARTER' },
  workspaceIds: [],
  searchTerm: '',
  includeDeletedMocks: true,
};

export const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100] as const;
export type PageSize = typeof PAGE_SIZE_OPTIONS[number];

export const DEFAULT_PAGE_SIZE: PageSize = 10;
export const DEFAULT_SORT: UsageReportSort = { field: 'DEPLOYED_AT', direction: 'DESC' };
